import {
    DataCollectionGapSummary,
    DataCollectionGapSummaryComplianceRegulation,
    DataCollectionGapSummaryMaterial,
    DataCollectionGapSummaryQuestionnaire
} from '../../../../../store/Models/Api/DataCollectionGapSummaryModels';

export const MaterialPageSize = 25;

export const getRequiredQuestionsUnanswered = (
    requiredQuestionsTotal?: number,
    requiredQuestionsAnswered?: number
) => {
    return Math.max(getRequiredQuestionsTotalValue(requiredQuestionsTotal) - getRequiredQuestionsAnsweredValue(requiredQuestionsTotal, requiredQuestionsAnswered), 0);
};

export const getRequiredQuestionsTotalValue = (requiredQuestionsTotal?: number) => {
    return requiredQuestionsTotal ?? 0;
};

export const getRequiredQuestionsAnsweredValue = (
    requiredQuestionsTotal?: number,
    requiredQuestionsAnswered?: number
) => {
    return Math.min(requiredQuestionsAnswered ?? 0, getRequiredQuestionsTotalValue(requiredQuestionsTotal));
};

export const hasQuestionnaireGaps = (questionnaire: DataCollectionGapSummaryQuestionnaire) => {
    return getQuestionnaireRequiredQuestionUnanswered(questionnaire) > 0;
};

export const hasRegulationGaps = (regulation: DataCollectionGapSummaryComplianceRegulation) => {
    return getRegulationRequiredQuestionUnanswered(regulation) > 0;
};

export const hasMaterialGaps = (material: DataCollectionGapSummaryMaterial) => {
    const regulationGaps = material.complianceRegulations?.some(hasRegulationGaps) ?? false;
    const questionnaireGaps = material.customQuestionnaires?.some(hasQuestionnaireGaps) ?? false;
    const unansweredRequiredBomColumns = material.bomDataValidation?.unansweredRequiredColumnNames?.length ?? 0;

    return regulationGaps || questionnaireGaps || unansweredRequiredBomColumns > 0;
};

export const formatMaterialName = (material: DataCollectionGapSummaryMaterial) => {
    return material.partName || material.description || material.inventoryId;
};

export const formatMaterialDescription = (material: DataCollectionGapSummaryMaterial) => {
    const materialName = formatMaterialName(material);

    return material.description && material.description !== materialName
        ? material.description
        : null;
};

export const getMaterialRequiredQuestionTotal = (material: DataCollectionGapSummaryMaterial) => {
    return (material.complianceRegulations ?? []).reduce(
        (total, regulation) => total + (regulation.requiredQuestionsTotal ?? 0),
        0)
        + (material.customQuestionnaires ?? []).reduce(
            (total, questionnaire) => total + (questionnaire.requiredQuestionsTotal ?? 0),
            0);
};

export const getMaterialRequiredQuestionAnswered = (material: DataCollectionGapSummaryMaterial) => {
    return (material.complianceRegulations ?? []).reduce(
        (total, regulation) => total + (regulation.requiredQuestionsAnswered ?? 0),
        0)
        + (material.customQuestionnaires ?? []).reduce(
            (total, questionnaire) => total + (questionnaire.requiredQuestionsAnswered ?? 0),
            0);
};

export const getMaterialRequiredQuestionUnanswered = (material: DataCollectionGapSummaryMaterial) => {
    return getRequiredQuestionsUnanswered(
        getMaterialRequiredQuestionTotal(material),
        getMaterialRequiredQuestionAnswered(material));
};

export const getMaterialUnansweredCount = (material: DataCollectionGapSummaryMaterial) => {
    const unansweredRequiredBomColumns = material.bomDataValidation?.unansweredRequiredColumnNames?.length ?? 0;

    return getMaterialRequiredQuestionUnanswered(material) + unansweredRequiredBomColumns;
};

export const getQuestionnaireRequiredQuestionUnanswered = (questionnaire: DataCollectionGapSummaryQuestionnaire) => {
    return getRequiredQuestionsUnanswered(
        questionnaire.requiredQuestionsTotal,
        questionnaire.requiredQuestionsAnswered);
};

export const getQuestionnaireRequiredQuestionAnswered = (questionnaire: DataCollectionGapSummaryQuestionnaire) => {
    return getRequiredQuestionsAnsweredValue(
        questionnaire.requiredQuestionsTotal,
        questionnaire.requiredQuestionsAnswered);
};

export const getRegulationRequiredQuestionUnanswered = (regulation: DataCollectionGapSummaryComplianceRegulation) => {
    return getRequiredQuestionsUnanswered(
        regulation.requiredQuestionsTotal,
        regulation.requiredQuestionsAnswered);
};

export const getRegulationRequiredQuestionAnswered = (regulation: DataCollectionGapSummaryComplianceRegulation) => {
    return getRequiredQuestionsAnsweredValue(
        regulation.requiredQuestionsTotal,
        regulation.requiredQuestionsAnswered);
};

export const getRequiredQuestionsTotal = (summary: DataCollectionGapSummary | null | undefined) => {
    const supplierTotal = summary?.supplierQuestionnaires?.reduce(
        (total, questionnaire) => total + (questionnaire.requiredQuestionsTotal ?? 0),
        0) ?? 0;

    const materialTotal = summary?.materialData?.reduce(
        (total, material) => total
            + getMaterialRequiredQuestionTotal(material)
            + (material.bomDataValidation?.unansweredRequiredColumnNames?.length ?? 0),
        0) ?? 0;

    return supplierTotal + materialTotal;
};

export const getRequiredQuestionsAnswered = (summary: DataCollectionGapSummary | null | undefined) => {
    const supplierAnswered = summary?.supplierQuestionnaires?.reduce(
        (total, questionnaire) => total + (questionnaire.requiredQuestionsAnswered ?? 0),
        0) ?? 0;

    const materialAnswered = summary?.materialData?.reduce(
        (total, material) => total + getMaterialRequiredQuestionAnswered(material),
        0) ?? 0;

    return supplierAnswered + materialAnswered;
};

// The helpers below mirror the rows emitted by the server-side gap report CSV generator (unanswered
// questions plus failed BOM/material-disclosure validation) so the report panel metrics and the default
// email counts match the attached report exactly.

const countQuestionnaireQuestions = (questionnaire: DataCollectionGapSummaryQuestionnaire) => {
    return (questionnaire.pages ?? []).reduce(
        (total, page) => total + (page.questions?.length ?? 0),
        0);
};

export const materialHasBomGap = (material: DataCollectionGapSummaryMaterial) => {
    return material.bomDataValidation != null && material.bomDataValidation.fieldsStatus === false;
};

const getMaterialBomGapRowCount = (material: DataCollectionGapSummaryMaterial) => {
    if (!materialHasBomGap(material)) {
        return 0;
    }

    // One row per validation note, or a single row when validation failed without notes.
    const noteCount = material.bomDataValidation?.notes?.length ?? 0;
    return noteCount === 0 ? 1 : noteCount;
};

export const hasMaterialGapsIncludingBom = (material: DataCollectionGapSummaryMaterial) => {
    return hasMaterialGaps(material) || materialHasBomGap(material);
};

export const getGapReportMaterialsWithGapsCount = (summary: DataCollectionGapSummary | null | undefined) => {
    return (summary?.materialData ?? []).filter(hasMaterialGapsIncludingBom).length;
};

export const getGapReportMissingFieldCount = (summary: DataCollectionGapSummary | null | undefined) => {
    const supplierFields = (summary?.supplierQuestionnaires ?? []).reduce(
        (total, questionnaire) => total + countQuestionnaireQuestions(questionnaire),
        0);

    const materialFields = (summary?.materialData ?? []).reduce(
        (total, material) => total
            + (material.complianceRegulations ?? []).reduce((sum, regulation) => sum + (regulation.questions?.length ?? 0), 0)
            + (material.customQuestionnaires ?? []).reduce((sum, questionnaire) => sum + countQuestionnaireQuestions(questionnaire), 0)
            + getMaterialBomGapRowCount(material),
        0);

    return supplierFields + materialFields;
};
